/**
 * Export Service
 * Utilities for exporting user data and analytics in various formats
 */

import { exportToExcel, exportToExcelMultiSheet } from './excelExport';
import { exportElementToPDF, exportTableToPDF, exportReportToPDF } from './pdfExport';
import { exportChartsToPowerPoint, exportTableToPowerPoint, exportReportToPowerPoint } from './powerpointExport';
import { exportToParquet, exportToParquetSimplified } from './parquetExport';

/**
 * Convert data to CSV format with enhanced formatting
 * @param {Array} data - Array of objects to convert
 * @param {Array} columns - Array of column definitions [{ key, label, format }]
 * @param {Object} options - CSV export options
 * @returns {string} - CSV string
 */
export const convertToCSV = (data, columns, options = {}) => {
  if (!data || data.length === 0) {
    return '';
  }

  const {
    includeBOM = false, // Include UTF-8 BOM for Excel compatibility
    delimiter = ',',
    lineEnding = '\n',
    dateFormat = 'YYYY-MM-DD',
    numberFormat = 'en-US'
  } = options;

  // Create header row with proper escaping
  const headers = columns.map(col => {
    const header = col.label || col.key;
    return escapeCSVValue(header);
  }).join(delimiter);
  
  // Create data rows with formatting
  const rows = data.map(item => {
    return columns.map(col => {
      const value = getNestedValue(item, col.key);
      const formattedValue = formatCSVValue(value, col.format, { dateFormat, numberFormat });
      return escapeCSVValue(formattedValue);
    }).join(delimiter);
  });
  
  const csvContent = [headers, ...rows].join(lineEnding);
  
  // Add UTF-8 BOM if requested (helps Excel open files correctly)
  return includeBOM ? '\uFEFF' + csvContent : csvContent;
};

/**
 * Escape CSV value
 * @param {*} value - Value to escape
 * @returns {string} - Escaped CSV value
 */
const escapeCSVValue = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  
  const stringValue = String(value);
  
  // If value contains delimiter, quotes, or newlines, wrap in quotes and escape quotes
  if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n') || stringValue.includes('\r')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  
  return stringValue;
};

/**
 * Format value for CSV export
 * @param {*} value - Value to format
 * @param {string} format - Format type
 * @param {Object} options - Formatting options
 * @returns {string} - Formatted value
 */
const formatCSVValue = (value, format, options = {}) => {
  if (value === null || value === undefined) {
    return '';
  }

  const { dateFormat, numberFormat } = options;

  switch (format) {
    case 'currency':
      if (typeof value === 'number') {
        return new Intl.NumberFormat(numberFormat, {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(value);
      }
      return `$${parseFloat(value || 0).toFixed(2)}`;
    
    case 'percentage':
      if (typeof value === 'number') {
        return `${(value * 100).toFixed(2)}%`;
      }
      return `${(parseFloat(value || 0) * 100).toFixed(2)}%`;
    
    case 'date':
      if (value instanceof Date) {
        return formatDate(value, dateFormat);
      }
      return formatDate(new Date(value), dateFormat);
    
    case 'number':
      if (typeof value === 'number') {
        return new Intl.NumberFormat(numberFormat).format(value);
      }
      return parseFloat(value || 0).toLocaleString(numberFormat);
    
    case 'boolean':
      return value ? 'Yes' : 'No';
    
    default:
      return String(value);
  }
};

/**
 * Format date according to format string
 * @param {Date} date - Date to format
 * @param {string} format - Format string (e.g., 'YYYY-MM-DD', 'MM/DD/YYYY')
 * @returns {string} - Formatted date
 */
const formatDate = (date, format) => {
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return format
    .replace('YYYY', year)
    .replace('MM', month)
    .replace('DD', day)
    .replace('HH', hours)
    .replace('mm', minutes)
    .replace('ss', seconds);
};

/**
 * Convert data to JSON format with enhanced formatting
 * @param {Array|Object} data - Data to convert
 * @param {Object} options - JSON export options
 * @returns {string} - JSON string
 */
export const convertToJSON = (data, options = {}) => {
  const {
    indent = 2,
    includeMetadata = true,
    dateFormat = 'ISO',
    prettyPrint = true
  } = options;

  // Add metadata if requested
  let exportData = data;
  if (includeMetadata && Array.isArray(data)) {
    exportData = {
      metadata: {
        exportDate: new Date().toISOString(),
        recordCount: data.length,
        version: '1.0'
      },
      data: data
    };
  }

  // Custom replacer for dates and special values
  const replacer = (key, value) => {
    if (value instanceof Date) {
      if (dateFormat === 'ISO') {
        return value.toISOString();
      } else if (dateFormat === 'timestamp') {
        return value.getTime();
      } else {
        return value.toLocaleString();
      }
    }
    // Handle undefined values
    if (value === undefined) {
      return null;
    }
    return value;
  };

  if (prettyPrint) {
    return JSON.stringify(exportData, replacer, indent);
  } else {
    return JSON.stringify(exportData, replacer);
  }
};

/**
 * Get nested value from object using dot notation
 * @param {Object} obj - Object to get value from
 * @param {string} path - Dot notation path (e.g., 'user.email')
 * @returns {*} - Value at path or null
 */
const getNestedValue = (obj, path) => {
  if (!obj || !path) return null;
  
  const keys = path.split('.');
  let value = obj;
  
  for (const key of keys) {
    if (value === null || value === undefined) {
      return null;
    }
    value = value[key];
  }
  
  return value;
};

/**
 * Download data as file
 * @param {string} content - File content
 * @param {string} filename - Filename
 * @param {string} mimeType - MIME type (e.g., 'text/csv', 'application/json')
 */
export const downloadFile = (content, filename, mimeType = 'text/csv') => {
  const blob = new Blob([content], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Export user list to CSV with enhanced formatting
 * @param {Array} users - Array of user objects
 * @param {string} filename - Optional filename (default: 'users-export.csv')
 * @param {Object} options - CSV export options
 */
export const exportUsersToCSV = (users, filename = 'users-export.csv', options = {}) => {
  const columns = [
    { key: 'userId', label: 'User ID' },
    { key: 'email', label: 'Email' },
    { key: 'name', label: 'Name' },
    { key: 'signupDate', label: 'Signup Date', format: 'date' },
    { key: 'daysSinceSignup', label: 'Days Since Signup', format: 'number' },
    { key: 'lastLoginDate', label: 'Last Login', format: 'date' },
    { key: 'daysSinceLastLogin', label: 'Days Since Last Login', format: 'number' },
    { key: 'isVerified', label: 'Verified', format: 'boolean' },
    { key: 'isOnboarded', label: 'Onboarded', format: 'boolean' },
    { key: 'hasWallet', label: 'Has Wallet', format: 'boolean' },
    { key: 'splitCount', label: 'Split Count', format: 'number' },
    { key: 'transactionCount', label: 'Transaction Count', format: 'number' },
    { key: 'transactionValue', label: 'Transaction Value', format: 'currency' },
    { key: 'badgeCount', label: 'Badge Count', format: 'number' }
  ];
  
  const csv = convertToCSV(users, columns, { includeBOM: true, ...options });
  downloadFile(csv, filename, 'text/csv;charset=utf-8');
};

/**
 * Export user list to JSON with enhanced formatting
 * @param {Array} users - Array of user objects
 * @param {string} filename - Optional filename (default: 'users-export.json')
 * @param {Object} options - JSON export options
 */
export const exportUsersToJSON = (users, filename = 'users-export.json', options = {}) => {
  const json = convertToJSON(users, { includeMetadata: true, ...options });
  downloadFile(json, filename, 'application/json');
};

/**
 * Export segment data
 * @param {Object} segmentData - Segment data object
 * @param {string} segmentName - Name of the segment
 * @param {string} format - Export format ('csv' | 'json')
 */
export const exportSegment = (segmentData, segmentName, format = 'csv') => {
  const timestamp = new Date().toISOString().split('T')[0];
  const sanitizedName = segmentName.toLowerCase().replace(/[^a-z0-9]/g, '-');
  const filename = `${sanitizedName}-${timestamp}.${format}`;
  
  if (format === 'json') {
    exportUsersToJSON(segmentData, filename);
  } else {
    exportUsersToCSV(segmentData, filename);
  }
};

/**
 * Export analytics report
 * @param {Object} reportData - Report data object
 * @param {string} reportName - Name of the report
 * @param {string} format - Export format ('csv' | 'json')
 */
export const exportReport = (reportData, reportName, format = 'json') => {
  const timestamp = new Date().toISOString().split('T')[0];
  const sanitizedName = reportName.toLowerCase().replace(/[^a-z0-9]/g, '-');
  const filename = `${sanitizedName}-${timestamp}.${format}`;
  
  if (format === 'json') {
    const json = convertToJSON(reportData);
    downloadFile(json, filename, 'application/json');
  } else {
    // For CSV, try to flatten the report data
    const flattened = flattenObject(reportData);
    const columns = Object.keys(flattened[0] || {}).map(key => ({ key, label: key }));
    const csv = convertToCSV(flattened, columns);
    downloadFile(csv, filename, 'text/csv');
  }
};

/**
 * Flatten nested object for CSV export
 * @param {Object} obj - Object to flatten
 * @param {string} prefix - Prefix for nested keys
 * @returns {Object} - Flattened object
 */
const flattenObject = (obj, prefix = '') => {
  const flattened = {};
  
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const newKey = prefix ? `${prefix}.${key}` : key;
      const value = obj[key];
      
      if (value !== null && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
        Object.assign(flattened, flattenObject(value, newKey));
      } else {
        flattened[newKey] = value;
      }
    }
  }
  
  return flattened;
};

/**
 * Export data to CSV (generic wrapper) with enhanced formatting
 * @param {Array} data - Array of objects to export
 * @param {string} filename - Filename
 * @param {Array} [columns] - Optional column definitions
 * @param {Object} [options] - CSV export options
 */
export const exportToCsv = (data, filename, columns = null, options = {}) => {
  if (!data || data.length === 0) {
    console.warn('No data to export');
    return;
  }

  // If columns provided, use them; otherwise infer from first object
  const exportColumns = columns || Object.keys(data[0] || {}).map(key => ({ key, label: key }));
  const csv = convertToCSV(data, exportColumns, { includeBOM: true, ...options });
  downloadFile(csv, filename, 'text/csv;charset=utf-8');
};

/**
 * Export data to JSON (generic wrapper) with enhanced formatting
 * @param {Array|Object} data - Data to export
 * @param {string} filename - Filename
 * @param {Object} [options] - JSON export options
 */
export const exportToJson = (data, filename, options = {}) => {
  const json = convertToJSON(data, { includeMetadata: true, ...options });
  downloadFile(json, filename, 'application/json');
};

/**
 * Export data to Excel format
 * @param {Array} data - Array of objects to export
 * @param {string} filename - Filename (without extension)
 * @param {Array} columns - Column definitions
 * @param {Object} options - Export options
 */
export const exportToExcelFormat = (data, filename, columns = null, options = {}) => {
  return exportToExcel(data, filename, columns, options);
};

/**
 * Export multiple datasets to Excel with multiple sheets
 * @param {Array} sheets - Array of { name, data, columns, options }
 * @param {string} filename - Filename (without extension)
 */
export const exportToExcelMultiSheetFormat = (sheets, filename) => {
  return exportToExcelMultiSheet(sheets, filename);
};

/**
 * Export HTML element to PDF
 * @param {HTMLElement} element - Element to export
 * @param {string} filename - Filename (without extension)
 * @param {Object} options - Export options
 */
export const exportElementToPDFFormat = async (element, filename, options = {}) => {
  return await exportElementToPDF(element, filename, options);
};

/**
 * Export data table to PDF
 * @param {Array} data - Array of objects to export
 * @param {Array} columns - Column definitions
 * @param {string} filename - Filename (without extension)
 * @param {Object} options - Export options
 */
export const exportTableToPDFFormat = (data, columns, filename, options = {}) => {
  return exportTableToPDF(data, columns, filename, options);
};

/**
 * Export report to PDF
 * @param {Object} reportData - Report data object
 * @param {string} filename - Filename (without extension)
 * @param {Object} options - Export options
 */
export const exportReportToPDFFormat = (reportData, filename, options = {}) => {
  return exportReportToPDF(reportData, filename, options);
};

/**
 * Export progress callback type
 * @callback ExportProgressCallback
 * @param {number} progress - Progress percentage (0-100)
 * @param {string} message - Progress message
 */

/**
 * Batch export multiple datasets
 * @param {Array} exports - Array of export configurations
 * @param {ExportProgressCallback} onProgress - Progress callback
 * @returns {Promise<void>}
 */
export const batchExport = async (exports, onProgress = null) => {
  const total = exports.length;
  let completed = 0;

  for (const exportConfig of exports) {
    try {
      const { type, data, filename, columns, options } = exportConfig;
      
      switch (type) {
        case 'csv':
          exportToCsv(data, filename, columns);
          break;
        case 'json':
          exportToJson(data, filename);
          break;
        case 'excel':
          exportToExcelFormat(data, filename, columns, options);
          break;
        case 'pdf':
          if (exportConfig.element) {
            await exportElementToPDFFormat(exportConfig.element, filename, options);
          } else {
            exportTableToPDFFormat(data, columns, filename, options);
          }
          break;
        case 'powerpoint':
          if (exportConfig.charts) {
            await exportToPowerPoint(exportConfig.charts, filename, options);
          } else if (exportConfig.exportType === 'report') {
            await exportReportToPowerPointFormat(data, filename, options);
          } else {
            await exportTableToPowerPointFormat(data, columns, filename, options);
          }
          break;
        case 'parquet':
          await exportToParquetFormat(data, filename, options);
          break;
        default:
          console.warn(`Unknown export type: ${type}`);
      }

      completed++;
      if (onProgress) {
        onProgress((completed / total) * 100, `Exported ${completed} of ${total} files`);
      }
    } catch (error) {
      console.error(`Error exporting ${exportConfig.filename}:`, error);
      if (onProgress) {
        onProgress((completed / total) * 100, `Error exporting ${exportConfig.filename}`);
      }
    }
  }

  if (onProgress) {
    onProgress(100, 'All exports completed');
  }
};

/**
 * Export data in multiple formats simultaneously
 * @param {Array|Object} data - Data to export
 * @param {string} baseFilename - Base filename (without extension)
 * @param {Array} formats - Array of format strings ('csv', 'json', 'excel', 'pdf')
 * @param {Array} columns - Column definitions (for table formats)
 * @param {Object} options - Export options
 * @param {ExportProgressCallback} onProgress - Progress callback
 */
export const exportMultipleFormats = async (
  data, 
  baseFilename, 
  formats = ['csv', 'json'], 
  columns = null,
  options = {},
  onProgress = null
) => {
  const exports = formats.map(format => ({
    type: format,
    data,
    filename: `${baseFilename}.${format}`,
    columns,
    options
  }));

  await batchExport(exports, onProgress);
};

/**
 * Export charts as PowerPoint slides
 * @param {Array} charts - Array of chart configurations { element, title, description }
 * @param {string} filename - Filename (without extension)
 * @param {Object} options - Export options
 */
export const exportToPowerPoint = async (charts, filename = 'presentation', options = {}) => {
  return await exportChartsToPowerPoint(charts, filename, options);
};/**
 * Export table to PowerPoint
 * @param {Array} data - Array of objects to export
 * @param {Array} columns - Column definitions
 * @param {string} filename - Filename (without extension)
 * @param {Object} options - Export options
 */
export const exportTableToPowerPointFormat = async (data, columns, filename, options = {}) => {
  return await exportTableToPowerPoint(data, columns, filename, options);
};/**
 * Export report to PowerPoint
 * @param {Object} reportData - Report data object
 * @param {string} filename - Filename (without extension)
 * @param {Object} options - Export options
 */
export const exportReportToPowerPointFormat = async (reportData, filename, options = {}) => {
  return await exportReportToPowerPoint(reportData, filename, options);
};/**
 * Export data to Parquet format
 * @param {Array} data - Array of objects to export
 * @param {string} filename - Filename (without extension)
 * @param {Object} options - Export options
 */
export const exportToParquetFormat = async (data, filename = 'export', options = {}) => {
  return await exportToParquet(data, filename, options);
};